import React, { useState, useEffect, useCallback } from 'react';
import { Modal, List, Button, Input, message, Typography, Divider, Icon, Skeleton, Spin } from 'antd';
import moment from 'moment';

const { Text } = Typography;

const CommentsModal = ({ 
  visible, 
  onCancel, 
  recordTaskId, 
  record, 
  monthIndex, 
  addComment, 
  createTaskRecordAndComment, 
  removeComment,
  data, 
  setCustomers,
  customers,
  comments = []
}) => {
  const [newComment, setNewComment] = useState('');
  const [addingLoading, setAddingLoading] = useState(false);
  const [removingLoading, setRemovingLoading] = useState({});
  const [currentComments, setCurrentComments] = useState(comments);
  const [initialLoading, setInitialLoading] = useState(true);

  // Update currentComments when comments prop changes
  useEffect(() => {
    setCurrentComments(comments);
    // Simulate initial loading delay for better UX
    const timer = setTimeout(() => {
      setInitialLoading(false);
    }, 500);
    return () => clearTimeout(timer);
  }, [comments]);

  const handleAddComment = useCallback(async () => {
    if (!newComment.trim()) {
      message.error('Por favor, insira um comentário');
      return;
    }

    try {
      setAddingLoading(true);
      let updatedComments;
      if (recordTaskId) {
        updatedComments = await addComment(recordTaskId, newComment, setCustomers);
      } else {
        if (!data || !record || !record.customer_id || !record.due_date || monthIndex === null) {
          message.error('Dados inválidos para criar o registro');
          return;
        }
        updatedComments = await createTaskRecordAndComment(newComment, record.customer_id, record, monthIndex, data, setCustomers);
      }
      
      setCurrentComments(updatedComments);
      setNewComment('');
      message.success('Comentário adicionado com sucesso');
    } catch (error) {
      message.error('Erro ao adicionar comentário');
    } finally {
      setAddingLoading(false);
    }
  }, [newComment, recordTaskId, record, monthIndex, addComment, createTaskRecordAndComment, data, setCustomers]);

  const handleRemoveComment = async (comment) => {
    try {
      setRemovingLoading(prev => ({ ...prev, [comment.uid]: true }));
      
      const updatedComments = await removeComment(comment, customers, setCustomers);
      setCurrentComments(updatedComments);
      message.success('Comentário removido com sucesso');
    } catch (error) {
      message.error('Erro ao remover comentário');
    } finally {
      setRemovingLoading(prev => ({ ...prev, [comment.uid]: false }));
    }
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Icon type="message" style={{ fontSize: 20 }} />
          <span>Comentários</span>
        </div>
      }
      visible={visible}
      onCancel={onCancel}
      footer={null}
      width={600}
      bodyStyle={{ padding: '24px 24px 24px' }}
    >
      <div style={{ marginBottom: 24 }}>
        <Input.TextArea
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          placeholder="Digite seu comentário aqui..."
          autoSize={{ minRows: 3, maxRows: 6 }}
          count={{ show: true, max: 500 }}
          disabled={addingLoading}
          style={{ marginBottom: 8 }}
        />
        <Button 
          type="primary" 
          onClick={handleAddComment}
          loading={addingLoading}
          disabled={addingLoading || !newComment.trim()}
          icon="send"
          style={{ width: '100%' }}
        >
          Enviar Comentário
        </Button>
      </div>

      <Divider style={{ margin: '16px 0' }} />

      {initialLoading ? (
        <List
          dataSource={Array(3).fill(null)}
          renderItem={() => (
            <List.Item>
              <Skeleton
                active
                paragraph={{ rows: 2 }}
                title={false}
              />
            </List.Item>
          )}
        />
      ) : (
        <List
          dataSource={currentComments}
          loading={addingLoading}
          locale={{ emptyText: 'Nenhum comentário ainda' }}
          renderItem={(item) => (
            <List.Item
              style={{
                background: '#fafafa',
                borderRadius: '8px',
                marginBottom: '12px',
                padding: '12px 16px',
                border: '1px solid #f0f0f0',
                position: 'relative'
              }}
              actions={[
                removingLoading[item.uid] ? (
                  <Spin size="small" style={{ marginTop: 12 }} />
                ) : (
                  <Icon
                    type="delete"
                    onClick={() => handleRemoveComment(item)}
                    style={{ 
                      color: '#ff4d4f',
                      fontSize: '16px',
                      cursor: 'pointer',
                      marginTop: 12
                    }}
                  />
                )
              ]}
            >
              <div style={{ width: '100%', paddingTop: 20 }}>
                <Text 
                  type="secondary" 
                  style={{ 
                    fontSize: '12px',
                    position: 'absolute',
                    top: 12,
                    right: 16
                  }}
                >
                  {moment(item.created_at).fromNow()}
                </Text>
                <div>{item.comment}</div>
              </div>
            </List.Item>
          )}
        />
      )}
    </Modal>
  );
};

export default CommentsModal; 